import React, { useCallback, useRef, useState } from 'react';

import { AnimatePresence, type HTMLMotionProps } from 'framer-motion';

import { EmojiPicker, type EmojiPickerProps, PortalArea } from '@pkg/components';
import {
    type KeyPressCallback,
    type OutsideClickCallback,
    type OutsideClickExclusion,
    useKeyPress,
    useOutsideClick,
} from '@pkg/hooks';

import { Portal } from '../Portal';

import { ReactComponent as SmileyIcon } from './smiley.svg';

import { Action, EmojiPopover, IconWrapper, StyledEmojiPickerCombo } from './styles';

export interface EmojiPickerComboProps {
    closeExclusions?: OutsideClickExclusion;
    onSelectEmoji: EmojiPickerProps['onSelectEmoji'];
    // This prop is utilized to retrieve internal state (`show` vs `hidden`).
    onPopoverAnimate?: HTMLMotionProps<'div'>['onAnimationComplete'];
    top?: number;
    right?: number;
}

export function EmojiPickerCombo({
    closeExclusions,
    onSelectEmoji,
    onPopoverAnimate,
    top,
    right,
}: EmojiPickerComboProps) {
    const [popoverOpen, setPopoverOpen] = useState(false);
    const popoverRef = useRef<HTMLDivElement>(null);

    function handlePopoverToggle() {
        setPopoverOpen((open) => !open);
    }

    const handleEscape: KeyPressCallback = useCallback((event) => {
        event.preventDefault();
        setPopoverOpen(false);
    }, []);

    useKeyPress(['Escape'], handleEscape, {
        target: popoverRef?.current,
        disabled: !popoverOpen,
    });

    const handlePopoverClose: OutsideClickCallback = useCallback(() => setPopoverOpen(false), []);

    useOutsideClick(popoverRef?.current, handlePopoverClose, {
        disabled: !popoverOpen,
        exclude: closeExclusions,
    });

    const emojiPickerMarkup = popoverOpen ? (
        <EmojiPopover
            ref={popoverRef}
            key="EmojiPickerCombo-Popover"
            onAnimationComplete={onPopoverAnimate}
            $top={top}
            $right={right}
        >
            <EmojiPicker darkMode onSelectEmoji={onSelectEmoji} />
        </EmojiPopover>
    ) : null;

    return (
        <StyledEmojiPickerCombo>
            <Action aria-pressed={popoverOpen ? true : undefined} onClick={handlePopoverToggle}>
                <IconWrapper>
                    <SmileyIcon />
                </IconWrapper>
            </Action>

            <Portal destination={PortalArea.Transition}>
                <AnimatePresence>{emojiPickerMarkup}</AnimatePresence>
            </Portal>
        </StyledEmojiPickerCombo>
    );
}
